import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse, AxiosResponseHeaders } from "axios";
import { Thunk } from "../../store";
import {
  BackendObtenerEstadoUsuario,
  getCurrentDomain,
} from "../../../apis/backend/Api";
import mock_Json from "../../../dataJson/obtenerEstadoUsuario.json";
import { formatearMensajeBackend } from "../../../apis/helper/helperApi";
import { TypeStatus } from "../../helpers/constantes";
import {
  setDisponible,
  setDisponibleGuardia,
} from "./actualizarDisponibilidad";
import { setTipoHorario, setGuardiaActiva } from "./login";

interface State {
  obtenerEstadoUsuario: any[];
  status: TypeStatus;
  isLoading: boolean;
  Success: boolean;
  Mensaje: string | null;
  error: string | null;
}

const initialState: State = {
  obtenerEstadoUsuario: [],
  status: "idle",
  isLoading: false,
  Success: false,
  Mensaje: "",
  error: null,
};
interface defaultResponse {
  data: any;
  status: number;
  statusText: string;
  headers: AxiosResponseHeaders | {};
  config: any;
  request?: any;
}
const defaultResp: defaultResponse = {
  data: undefined,
  status: 0,
  statusText: "",
  headers: {},
  config: undefined,
};
const obtenerEstadoUsuarioSlice = createSlice({
  name: "obtenerEstadoUsuario",
  initialState,
  reducers: {
    setObtenerEstadoUsuario: (state, action: PayloadAction<any[]>) => {
      state.obtenerEstadoUsuario = action.payload;
    },
    getObtenerEstadoUsuario: (state, action: PayloadAction<any[]>) => {
      state.obtenerEstadoUsuario = action.payload;
    },
    setClearObtenerEstadoUsuario: (state, action: PayloadAction<any[]>) => {
      state = initialState;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setStatus: (state, action: PayloadAction<TypeStatus>) => {
      state.status = action.payload;
    },
    setResult: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setSuccess: (state, action: PayloadAction<boolean>) => {
      state.Success = action.payload;
    },
    setMensaje: (state, action: PayloadAction<string>) => {
      state.Mensaje = action.payload;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
  },
});
export const setBackendObtenerEstadoUsuario =
  (): Thunk =>
  async (dispatch): Promise<AxiosResponse | AxiosError> => {
    dispatch(setIsLoading(true));

    try {
      const response: AxiosResponse = await BackendObtenerEstadoUsuario();

      const isError =
        response.data?.Success === false ||
        (response.data?.ResultadoSolicitud !== undefined &&
          response.data?.ResultadoSolicitud !== 200);

      if (isError) {
        dispatch(setIsLoading(false));
        dispatch(setStatus("failed"));
        dispatch(setSuccess(false));
      } else {
        dispatch(setObtenerEstadoUsuario(response.data.Data));

        if (response.data.Data && response.data.Data.length > 0) {
          const estadoActual = response.data.Data[0];
          dispatch(setDisponible(estadoActual.Disponible));
          dispatch(setDisponibleGuardia(estadoActual.DisponibleGuardia));
          dispatch(setTipoHorario(estadoActual.TipoHorario));
          dispatch(setGuardiaActiva(estadoActual.GuardiaActiva));
        }

        dispatch(setIsLoading(false));
        dispatch(setStatus("succeeded"));
        dispatch(setSuccess(true));
      }
      dispatch(setMensaje(formatearMensajeBackend(response.data.Mensaje)));
      return response.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(setSuccess(false));
      dispatch(setStatus("failed"));

      if (error.response?.status === 401) {
        dispatch(setStatus("noAutorice"));
        dispatch(
          setMensaje(
            formatearMensajeBackend(
              "Tu sesión ha expirado. Vuelve a iniciar sesión",
            ),
          ),
        );
      } else if (error.response) {
        // Hay respuesta del servidor, pero es otro error (400, 422, 500, etc.)
        console.error(
          "Error de servidor:",
          error.response.status,
          error.response.data,
        );

        const errorData = error.response.data;
        const mensajeBackend = errorData?.Mensaje;
        const resultadoSolicitud = errorData?.ResultadoSolicitud;

        if (
          mensajeBackend &&
          (resultadoSolicitud !== 200 || errorData?.Success === false)
        ) {
          dispatch(setMensaje(formatearMensajeBackend(mensajeBackend)));
        } else {
          dispatch(
            setMensaje(
              `Error ${error.response.status}: Ocurrió un problema en el servidor.`,
            ),
          );
        }
      } else if (error.request) {
        console.error("Error de conexión:", error.message);
        dispatch(
          setMensaje(
            "No se pudo conectar al servidor. Por favor, revisa tu conexión a internet o tu VPN.",
          ),
        );
      } else {
        console.error("Error de red o solicitud:", error.message);
        dispatch(
          setMensaje(
            "Ocurrió un problema con la red. Por favor, verifica tu conexión.",
          ),
        );
      }
      return error as AxiosError;
    } finally {
      if (getCurrentDomain() === "localhost") {
        dispatch(setIsLoading(true));
        let response: AxiosResponse = defaultResp;
        response.data = mock_Json;

        const mockData: any = mock_Json;
        const isErrorMock =
          mockData?.Success === false ||
          (mockData?.ResultadoSolicitud !== undefined &&
            mockData?.ResultadoSolicitud !== 200);

        if (isErrorMock) {
          dispatch(setIsLoading(false));
          dispatch(setStatus("failed"));
          dispatch(setSuccess(false));
        } else {
          dispatch(setObtenerEstadoUsuario(response.data.Data));

          if (response.data.Data && response.data.Data.length > 0) {
            const estadoActual = response.data.Data[0];
            dispatch(setDisponible(estadoActual.Disponible));
            dispatch(setDisponibleGuardia(estadoActual.DisponibleGuardia));
            dispatch(setTipoHorario(estadoActual.TipoHorario));
            dispatch(setGuardiaActiva(estadoActual.GuardiaActiva));
          }

          dispatch(setStatus("succeeded"));
          dispatch(setSuccess(true));
        }

        await import("../../../dataJson/401.json")
          .then((mock401) => {
            if (mock401.status === 401) {
              dispatch(setStatus("noAutorice"));
              dispatch(
                setMensaje(
                  formatearMensajeBackend(
                    "Tu sesión ha expirado. Vuelve a iniciar sesión",
                  ),
                ),
              );
            }
          })
          .catch(() => {});
      }
      dispatch(setIsLoading(false));
    }
  };

export const setBackendClearObtenerEstadoUsuario =
  (): Thunk =>
  async (dispatch): Promise<AxiosResponse | AxiosError> => {
    dispatch(setIsLoading(true));
    try {
      let response: AxiosResponse = defaultResp;
      dispatch(setObtenerEstadoUsuario([]));
      dispatch(setIsLoading(false));
      dispatch(setStatus("idle"));
      dispatch(setSuccess(false));
      dispatch(setMensaje(""));
      return response.data;
    } catch (error: any) {
      dispatch(setIsLoading(false));
      dispatch(setStatus("failed"));
      return error as AxiosError;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const {
  setObtenerEstadoUsuario,
  getObtenerEstadoUsuario,
  setClearObtenerEstadoUsuario,
  setIsLoading,
  setStatus,
  setResult,
  setSuccess,
  setMensaje,
  setError,
} = obtenerEstadoUsuarioSlice.actions;

export default obtenerEstadoUsuarioSlice.reducer;
